import { DateRange } from '@/dashboards/ModelContributions/components/ActionBar/DateRange';
import { Button, Typography, ValueToggle } from '@analytical-alley/ui';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@analytical-alley/ui/components/Popover';
import { Label } from 'flowbite-react';
import moment from 'moment';
import { useMemo, useState } from 'react';
import { HiOutlineCalendar, HiChevronDown } from 'react-icons/hi';

import {
    EXECUTIVE_SUMMARY_PRESET_LABELS,
    EXECUTIVE_SUMMARY_PRESETS,
} from '../../config/presets';
import { useExecutiveSummaryContext } from '../../context';
import { formatRangeLabel } from '../../helpers/formatRangeLabel';

const toDate = (urlDate: string): Date =>
    moment.utc(urlDate, 'DD-MM-YYYY').toDate();

export const ExecutiveSummaryDateCompare = () => {
    const [open, setOpen] = useState(false);
    const {
        primaryRange,
        comparisonRange,
        compareEnabled,
        showCompareToggle,
        preset,
        bounds,
        setPreset,
        setCompareEnabled,
        setPrimaryRange,
        setComparisonRange,
    } = useExecutiveSummaryContext();

    const minDate = useMemo(() => toDate(bounds.startDate), [bounds.startDate]);
    const maxDate = useMemo(() => toDate(bounds.endDate), [bounds.endDate]);

    const triggerLabel = compareEnabled && comparisonRange
        ? `${formatRangeLabel(primaryRange)} vs ${formatRangeLabel(comparisonRange)}`
        : formatRangeLabel(primaryRange);

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button color="outline" size="sm" pill className="gap-2">
                    <HiOutlineCalendar className="h-4 w-4 shrink-0" />
                    <span className="truncate">{triggerLabel}</span>
                    <HiChevronDown className="h-4 w-4 shrink-0" />
                </Button>
            </PopoverTrigger>
            <PopoverContent
                align="end"
                side="bottom"
                sideOffset={8}
                className="w-auto max-w-[min(90vw,32rem)]"
            >
                <div className="flex flex-col gap-4 text-white">
                    <div className="flex flex-col gap-2">
                        <Typography variant="bodyS" className="opacity-70">
                            Quick select
                        </Typography>
                        <div className="flex flex-col gap-1.5">
                            {EXECUTIVE_SUMMARY_PRESETS.map((presetId) => (
                                <Button
                                    key={presetId}
                                    color={
                                        preset === presetId
                                            ? 'light'
                                            : 'outline'
                                    }
                                    size="xs"
                                    pill
                                    className="justify-start"
                                    onClick={() => setPreset(presetId)}
                                >
                                    {EXECUTIVE_SUMMARY_PRESET_LABELS[presetId]}
                                </Button>
                            ))}
                        </div>
                    </div>

                    {showCompareToggle && (
                        <>
                            <div className="h-px bg-white/20" />

                            <div className="flex items-center justify-between gap-4">
                                <Typography
                                    variant="bodyS"
                                    className="opacity-90"
                                >
                                    Compare
                                </Typography>
                                <ValueToggle<'on' | 'off'>
                                    compact
                                    options={[
                                        { label: 'Off', value: 'off' },
                                        { label: 'On', value: 'on' },
                                    ]}
                                    value={compareEnabled ? 'on' : 'off'}
                                    onChange={(value) =>
                                        setCompareEnabled(value === 'on')
                                    }
                                />
                            </div>
                        </>
                    )}

                    <div className="flex flex-col gap-3">
                        <div className="flex flex-col gap-1.5">
                            <Label className="font-mono font-normal text-white">
                                Primary period
                            </Label>
                            <DateRange
                                minDate={minDate}
                                maxDate={maxDate}
                                searchStartDate={primaryRange.startDate}
                                searchEndDate={primaryRange.endDate}
                                onSetSearch={setPrimaryRange}
                            />
                        </div>

                        <div className="flex flex-col gap-1.5">
                            <Label className="font-mono font-normal text-white">
                                Comparison period
                            </Label>
                            <DateRange
                                disabled={!compareEnabled}
                                minDate={minDate}
                                maxDate={maxDate}
                                searchStartDate={
                                    comparisonRange?.startDate ?? undefined
                                }
                                searchEndDate={
                                    comparisonRange?.endDate ?? undefined
                                }
                                onSetSearch={setComparisonRange}
                            />
                        </div>
                    </div>
                </div>
            </PopoverContent>
        </Popover>
    );
};
